import {
  createContext,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Lets each table/chart inside an AI reply claim its 0-based position (source
 * order) and reach the reply's stored id, so an inline "Save PDF" button can
 * ask the server to render that exact element. Indices line up with the
 * backend's parse of the same markdown. Shared by the full AI Assistant chat
 * and the Maya sidebar.
 */
export interface PdfExportValue {
  /** Stored (numeric) id of the reply; null when it isn't saved. */
  messageId: number | null;
  /** Leave the per-element button out while the reply isn't saved (the
   *  sidebar), rather than offering one that can only say "save first". */
  hideUnsaved?: boolean;
  /** The next index of that kind — or, for a `key` that already claimed
   *  one, the same index again. */
  claim: (part: "table" | "chart", key: string) => number;
}

export const PdfExportContext = createContext<PdfExportValue | null>(null);

/**
 * The provider value for one reply. Counters reset whenever the content
 * changes — key the markdown subtree by content too, so every table/chart
 * re-claims its index from zero.
 */
export const usePdfExportValue = (
  messageId: number | null,
  content: string,
  { hideUnsaved = false }: { hideUnsaved?: boolean } = {},
): PdfExportValue => {
  const counters = useRef({ table: 0, chart: 0 });
  const claimed = useRef(new Map<string, number>());
  useMemo(() => {
    counters.current = { table: 0, chart: 0 };
    claimed.current = new Map();
  }, [content]);
  return useMemo<PdfExportValue>(
    () => ({
      messageId,
      hideUnsaved,
      claim: (part, key) => {
        const id = `${part}:${key}`;
        let index = claimed.current.get(id);
        if (index === undefined) {
          index = counters.current[part]++;
          claimed.current.set(id, index);
        }
        return index;
      },
    }),
    // `content` is a dependency on purpose: it resets the counters above.
    [messageId, content, hideUnsaved],
  );
};

/** This table/chart's position in its reply, claimed once on mount. Keyed by
 *  `useId` so StrictMode's second run of the initializer gets the same index
 *  instead of taking the next one. */
export const usePdfPartIndex = (part: "table" | "chart"): number => {
  const ctx = useContext(PdfExportContext);
  const key = useId();
  const [index] = useState(() => ctx?.claim(part, key) ?? 0);
  return index;
};
